import { FormattedHTMLMessage } from "react-intl";

import { SelectComponent } from "@/components/FormInputs/Select/SelectComponent";
import { useMessage } from "@/components/Message";
import { DEFAULT_LOCALE } from "@/constants";
import { type FormSelectChoice, type Locale, type VerificationService } from "@/lib/types/types";
import { getAvailableLocales } from "@/lib/ProgramTheme/programThemeGetters";
import { useProgramTheme } from "@/lib/state/contextHooks";

import { LocaleSymbol } from "./icons/LocaleSymbol";

interface ChangeLocaleProps {
  value: FormSelectChoice<Locale, string>;
  options: FormSelectChoice<Locale, string>[];
  onChange: (localeChoice: FormSelectChoice) => any;
}

export const ChangeLocaleComponent = ({ value, options, onChange }: ChangeLocaleProps) => {
  const { msg } = useMessage();
  const newOptions: FormSelectChoice[] = options.map((option) => ({
    value: option.value,
    label: msg({ id: `locales.${option.value}` }),
  }));
  const id = "changeLanguageSelector";

  return (
    <div className="sid-change-locale">
      <div className="sid-field__label sid-change-locale__label" id={`${id}-label`}>
        <FormattedHTMLMessage id="changeLanguage" defaultMessage="Change language" />
      </div>
      <SelectComponent
        className="sid-change-locale__select"
        options={newOptions}
        onChange={(locale: FormSelectChoice) => onChange(locale)}
        DisplayValueDecorator={<LocaleSymbol />}
        overrideInputClassName="sid-change-locale__input"
        value={newOptions.find((option) => option.value === (value && value.value))}
        id={id}
      />
    </div>
  );
};

/**
 * This V2 component wraps the original one
 * It only takes verificationService as a prop for easier/slimmer invocation and to ensure locale ends up in the viewModel
 */
interface ChangeLocaleV2Props {
  verificationService: VerificationService;
}

export const ChangeLocaleV2Component = ({ verificationService }: ChangeLocaleV2Props) => {
  const { msg } = useMessage();
  const programTheme = useProgramTheme();
  const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocales(programTheme).map(
    (locale) => ({
      value: locale,
      label: msg({ id: `locales.${locale}` }),
    }),
  );

  const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
    if (!localeChoice?.value) return;
    if (verificationService.viewModel.localeChoice.value === localeChoice.value) return;

    verificationService.updateLocale(
      localeChoice.value,
      verificationService.verificationResponse.segment,
    );
  };

  if (localeChoices.length <= 1) {
    return null;
  }

  const defaultLocaleChoice: FormSelectChoice<Locale, string> = {
    value: DEFAULT_LOCALE,
    label: "English",
  };

  return (
    <ChangeLocaleComponent
      options={localeChoices}
      value={verificationService.viewModel.localeChoice || defaultLocaleChoice}
      onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
    />
  );
};
